/**
 * Enhanced Simulation Loading Progress Component
 * Two-column layout with tips/challenges on left and progress steps on right
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Flex, VStack, HStack, Text, Icon, Progress,
  Heading
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { MdCheckCircle, MdLightbulb, MdFlag, MdAutorenew, MdHourglassEmpty } from 'react-icons/md';

const fadeInOut = keyframes`
  0% { opacity: 0; transform: translateY(10px); }
  15% { opacity: 1; transform: translateY(0); }
  85% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-10px); }
`;

const pulseAnimation = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

const spinAnimation = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

type SimulationType = 'conversation' | 'conversation_with_problems' | 'coding' | 'hre_enabled';

interface InitStep {
  id: string;
  label: string;
  maxTimeSeconds: number;
  status: 'pending' | 'active' | 'completed';
}

interface SimulationLoadingProgressProps {
  userGoalsCount?: number;
  simulationType?: 'conversation' | 'coding' | 'hybrid';
  enableRealWorldProblems?: boolean;
  useHRE?: boolean;
}

const PRO_TIPS = [
  "Balance empathy and results — your choices have ripple effects.",
  "Each conversation reveals clues to completing your objectives.",
  "The more natural and thoughtful your responses, the better your outcome.",
  "There's no single \"right\" answer — it's about how you lead under pressure."
];

const CHALLENGES = [
  "Complete all objectives within the given time.",
  "You can alternate between Conversations and some conversations could be interlinked.",
  "Every interaction moves you closer to success OR costs you valuable progress."
];

// Timing configurations based on simulation type
const TIMING_CONFIGS: Record<SimulationType, { blueprint: number; tasks: number; personas: number; conversations: number; finalize: number }> = {
  conversation: {
    blueprint: 5,
    tasks: 15,
    personas: 50,  // Longest step - agent persona generation
    conversations: 15,
    finalize: 5
    // Total: 90 seconds (1.5 minutes)
  },
  conversation_with_problems: {
    blueprint: 5,
    tasks: 15,
    personas: 55,  // Slightly longer with real-world problems
    conversations: 15,
    finalize: 10
    // Total: 100 seconds
  },
  coding: {
    blueprint: 5,
    tasks: 20,
    personas: 55,  // Agent persona generation
    conversations: 30,  // Coding problem generation takes longer
    finalize: 10
    // Total: 120 seconds (2 minutes)
  },
  hre_enabled: {
    blueprint: 5,
    tasks: 10,
    personas: 30,  // HRE is faster but still substantial
    conversations: 10,
    finalize: 5
    // Total: 60 seconds (1 minute)
  }
};

function determineSimulationType(
  simulationType?: string,
  enableRealWorldProblems?: boolean,
  useHRE?: boolean
): SimulationType {
  if (useHRE) {
    return 'hre_enabled';
  }
  
  if (simulationType === 'coding' || simulationType === 'hybrid') {
    return 'coding';
  }
  
  if (enableRealWorldProblems) {
    return 'conversation_with_problems';
  }
  
  return 'conversation';
}

export function SimulationLoadingProgress({ 
  userGoalsCount = 6,
  simulationType = 'conversation',
  enableRealWorldProblems = false,
  useHRE = false
}: SimulationLoadingProgressProps) {
  const simType = determineSimulationType(simulationType, enableRealWorldProblems, useHRE);
  const timingConfig = TIMING_CONFIGS[simType];
  
  const INIT_STEPS: InitStep[] = [
    { id: 'blueprint', label: 'Loading simulation structure', maxTimeSeconds: timingConfig.blueprint, status: 'pending' },
    { id: 'tasks', label: 'Setting up objectives and tasks', maxTimeSeconds: timingConfig.tasks, status: 'pending' },
    { id: 'personas', label: 'Gathering characters', maxTimeSeconds: timingConfig.personas, status: 'pending' },
    { id: 'conversations', label: 'Setting up conversations', maxTimeSeconds: timingConfig.conversations, status: 'pending' },
    { id: 'finalize', label: 'Finalizing simulation', maxTimeSeconds: timingConfig.finalize, status: 'pending' }
  ];
  
  const [steps, setSteps] = useState<InitStep[]>(INIT_STEPS);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [showingTips, setShowingTips] = useState(true);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Calculate total max time
  const totalMaxTime = INIT_STEPS.reduce((sum, step) => sum + step.maxTimeSeconds, 0);

  // Calculate current progress
  const calculateProgress = () => {
    const completedTime = steps
      .slice(0, currentStepIndex)
      .reduce((sum, step) => sum + step.maxTimeSeconds, 0);
    
    const currentStepProgress = currentStepIndex < steps.length
      ? Math.min(elapsedSeconds - completedTime, steps[currentStepIndex].maxTimeSeconds)
      : 0;
    
    return ((completedTime + currentStepProgress) / totalMaxTime) * 100;
  };

  const progress = calculateProgress();

  // Progress simulation through steps
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSeconds(prev => prev + 0.1);
    }, 100);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Update step statuses based on elapsed time
    let cumulativeTime = 0;
    const newSteps = steps.map((step, index) => {
      const stepEndTime = cumulativeTime + step.maxTimeSeconds;
      
      let status: 'pending' | 'active' | 'completed';
      if (elapsedSeconds >= stepEndTime) {
        status = 'completed';
      } else if (elapsedSeconds >= cumulativeTime) {
        status = 'active';
      } else {
        status = 'pending';
      }
      
      cumulativeTime = stepEndTime;
      return { ...step, status };
    });

    setSteps(newSteps);

    // Update current step index
    const activeIndex = newSteps.findIndex(s => s.status === 'active');
    if (activeIndex !== -1 && activeIndex !== currentStepIndex) {
      setCurrentStepIndex(activeIndex);
    } else if (newSteps.every(s => s.status === 'completed')) {
      setCurrentStepIndex(newSteps.length);
    }
  }, [elapsedSeconds]);

  // Rotate through tips and challenges
  useEffect(() => {
    const tipDuration = 5000; // 5 seconds per tip
    const interval = setInterval(() => {
      if (showingTips) {
        if (currentTipIndex < PRO_TIPS.length - 1) {
          setCurrentTipIndex(prev => prev + 1);
        } else {
          // Switch to challenges
          setShowingTips(false);
          setCurrentTipIndex(0);
        }
      } else {
        if (currentTipIndex < CHALLENGES.length - 1) {
          setCurrentTipIndex(prev => prev + 1);
        } else {
          // Loop back to tips
          setShowingTips(true);
          setCurrentTipIndex(0);
        }
      }
    }, tipDuration);

    return () => clearInterval(interval);
  }, [currentTipIndex, showingTips]);

  return (
    <Flex
      gap={8}
      w="full"
      minH="300px"
      p={6}
      borderTopWidth="1px"
      borderColor="gray.200"
      bg="gray.50"
      borderRadius="lg"
    >
      {/* LEFT SIDE - Tips and Challenges */}
      <VStack
        flex={1}
        align="stretch"
        spacing={4}
        justify="center"
        position="relative"
        minH="200px"
      >
        {showingTips ? (
          <Box
            key={`tip-${currentTipIndex}`}
            animation={`${fadeInOut} 5s ease-in-out`}
            position="absolute"
            w="full"
          >
            <VStack align="stretch" spacing={3}>
              <HStack spacing={2}>
                <Icon as={MdLightbulb} color="yellow.500" boxSize={5} />
                <Text fontSize="md" fontWeight="600" color="text.primary">
                  Pro Tip
                </Text>
              </HStack>
              <Text color="text.secondary" fontSize="md" lineHeight="1.7">
                {PRO_TIPS[currentTipIndex]}
              </Text>
              <HStack spacing={1} pt={2}>
                {PRO_TIPS.map((_, idx) => (
                  <Box
                    key={idx}
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg={idx === currentTipIndex ? 'yellow.500' : 'gray.300'}
                    transition="all 0.3s"
                  />
                ))}
              </HStack>
            </VStack>
          </Box>
        ) : (
          <Box
            key={`challenge-${currentTipIndex}`}
            animation={`${fadeInOut} 5s ease-in-out`}
            position="absolute"
            w="full"
          >
            <VStack align="stretch" spacing={3}>
              <HStack spacing={2}>
                <Icon as={MdFlag} color="orange.500" boxSize={5} />
                <Text fontSize="md" fontWeight="600" color="text.primary">
                  Your Challenge
                </Text>
              </HStack>
              <Text color="text.secondary" fontSize="md" lineHeight="1.7">
                {CHALLENGES[currentTipIndex].replace('all objectives', `all ${userGoalsCount} objectives`)}
              </Text>
              <HStack spacing={1} pt={2}>
                {CHALLENGES.map((_, idx) => (
                  <Box
                    key={idx}
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg={idx === currentTipIndex ? 'orange.500' : 'gray.300'}
                    transition="all 0.3s"
                  />
                ))}
              </HStack>
            </VStack>
          </Box>
        )}
      </VStack>

      {/* VERTICAL DIVIDER */}
      <Box w="1px" bg="gray.300" />

      {/* RIGHT SIDE - Progress Steps */}
      <VStack flex={1} align="stretch" spacing={4}>
        <VStack align="stretch" spacing={2}>
          <HStack justify="space-between">
            <Heading size="sm" fontWeight="600" color="text.primary">
              Initializing Simulation
            </Heading>
            <Text fontSize="xs" fontWeight="600" color="brand.500">
              {Math.round(progress)}%
            </Text>
          </HStack>
          
          <Progress
            value={progress}
            size="sm"
            colorScheme="brand"
            borderRadius="full"
            bg="gray.200"
            hasStripe
            isAnimated
          />
          
          <HStack justify="space-between">
            <Text fontSize="xs" color="text.secondary">
              Estimated: ~{totalMaxTime}s
            </Text>
            <Text fontSize="xs" color="text.secondary">
              {Math.round(elapsedSeconds)}s elapsed
            </Text>
          </HStack>
        </VStack>

        <VStack align="stretch" spacing={3} pt={2}>
          {steps.map((step, index) => (
            <HStack
              key={step.id}
              spacing={3}
              opacity={step.status === 'pending' ? 0.5 : 1}
              transition="all 0.3s"
            >
              {/* Status Icon */}
              {step.status === 'completed' ? (
                <Icon as={MdCheckCircle} color="green.500" boxSize={5} />
              ) : step.status === 'active' ? (
                <Icon
                  as={MdAutorenew}
                  color="brand.500"
                  boxSize={5}
                  animation={`${spinAnimation} 1s linear infinite`}
                />
              ) : (
                <Icon as={MdHourglassEmpty} color="gray.400" boxSize={5} />
              )}

              {/* Step Label */}
              <Text
                fontSize="sm"
                fontWeight={step.status === 'active' ? '600' : '500'}
                color={
                  step.status === 'completed'
                    ? 'green.600'
                    : step.status === 'active'
                    ? 'brand.600'
                    : 'text.secondary'
                }
                flex={1}
              >
                {step.label}
              </Text>

              {/* Timer for active step */}
              {step.status === 'active' && (
                <Text
                  fontSize="xs"
                  color="brand.500"
                  fontWeight="600"
                  animation={`${pulseAnimation} 1.5s ease-in-out infinite`}
                >
                  {Math.round(
                    step.maxTimeSeconds - 
                    (elapsedSeconds - steps.slice(0, index).reduce((sum, s) => sum + s.maxTimeSeconds, 0))
                  )}s
                </Text>
              )}

              {/* Checkmark for completed */}
              {step.status === 'completed' && (
                <Text fontSize="xs" color="green.600" fontWeight="600">
                  ✓
                </Text>
              )}
            </HStack>
          ))}
        </VStack>
      </VStack>
    </Flex>
  );
}

